import { catalogApi } from "../api/services";

const STORAGE_KEY = "sgoffice_guest_cart";
const VAT_RATE = 0.05;

export function loadRawGuestItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveRawGuestItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function clearRawGuestItems() {
  localStorage.removeItem(STORAGE_KEY);
}

function lineItemId(productId, variantId) {
  return `${productId}:${variantId || ""}`;
}

function parseLineItemId(itemId) {
  const [productId, ...rest] = String(itemId).split(":");
  return { productId, variantId: rest.join(":") || null };
}

function resolveLinePrice(product, variantId) {
  if (!variantId || !product?.variants?.length) {
    return Number(product?.price) || 0;
  }
  const variant = product.variants.find((v) => String(v.id) === String(variantId));
  if (!variant) return Number(product?.price) || 0;
  return variant.price != null ? Number(variant.price) : Number(product.price) || 0;
}

function resolveLineStock(product, variantId) {
  if (!variantId || !product?.variants?.length) {
    return Number(product?.stock) || 0;
  }
  const variant = product.variants.find((v) => String(v.id) === String(variantId));
  return variant ? Number(variant.stock) || 0 : Number(product?.stock) || 0;
}

function calcVat(subtotal) {
  const vatAmount = Math.round(subtotal * VAT_RATE * 100) / 100;
  const total = Math.round((subtotal + vatAmount) * 100) / 100;
  return { vatRate: VAT_RATE, vatAmount, total };
}

const emptyCart = {
  items: [],
  subtotal: 0,
  vatRate: VAT_RATE,
  vatAmount: 0,
  total: 0,
  currency: "AED",
};

export async function hydrateGuestCart() {
  const raw = loadRawGuestItems();
  if (!raw.length) return { ...emptyCart };

  const items = [];
  const validRaw = [];

  for (const row of raw) {
    try {
      const res = await catalogApi.getProduct(row.productId);
      const product = res.data.data.product;
      if (!product?.isActive) continue;

      const variantId = row.variantId || null;
      const stock = resolveLineStock(product, variantId);
      const quantity = Math.max(1, Number(row.quantity) || 1);
      const price = resolveLinePrice(product, variantId);
      const variant = variantId
        ? product.variants?.find((v) => String(v.id) === String(variantId))
        : null;
      const categoryName =
        typeof product.category === "object"
          ? product.category?.name
          : product.category || "";

      items.push({
        id: lineItemId(row.productId, variantId),
        productId: product.id,
        variantId,
        selectedAttributes: row.selectedAttributes || {},
        name: product.name,
        slug: product.slug,
        category: categoryName,
        price,
        quantity,
        image: variant?.image || product.images?.[0] || "",
        stock,
        currency: product.currency || "AED",
        sku: variant?.sku || product.sku || "",
      });
      validRaw.push({ ...row, quantity });
    } catch {
      // skip unavailable products
    }
  }

  if (validRaw.length !== raw.length) {
    saveRawGuestItems(validRaw);
  }

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const { vatRate, vatAmount, total } = calcVat(subtotal);
  return { items, subtotal, vatRate, vatAmount, total, currency: "AED" };
}

export async function addGuestCartItem(productId, quantity, extras = {}) {
  const res = await catalogApi.getProduct(productId);
  const product = res.data.data.product;
  if (!product?.isActive) throw new Error("Product not available");

  const variantId = extras.variantId || null;
  const selectedAttributes = extras.selectedAttributes || {};

  if (product.variantOptions?.length && !variantId) {
    throw new Error("Please select product options");
  }

  const raw = loadRawGuestItems();
  const key = lineItemId(productId, variantId);
  const existing = raw.find(
    (row) => lineItemId(row.productId, row.variantId) === key
  );

  if (existing) existing.quantity += quantity;
  else raw.push({ productId, variantId, selectedAttributes, quantity });

  saveRawGuestItems(raw);
  return hydrateGuestCart();
}

export async function updateGuestCartItem(itemId, quantity) {
  const { productId, variantId } = parseLineItemId(itemId);
  const raw = loadRawGuestItems();
  const index = raw.findIndex(
    (row) =>
      String(row.productId) === productId &&
      String(row.variantId || "") === String(variantId || "")
  );
  if (index === -1) throw new Error("Item not in cart");

  if (quantity < 1) raw.splice(index, 1);
  else raw[index].quantity = quantity;

  saveRawGuestItems(raw);
  return hydrateGuestCart();
}

export async function removeGuestCartItem(itemId) {
  const { productId, variantId } = parseLineItemId(itemId);
  const next = loadRawGuestItems().filter(
    (row) =>
      !(
        String(row.productId) === productId &&
        String(row.variantId || "") === String(variantId || "")
      )
  );
  saveRawGuestItems(next);
  return hydrateGuestCart();
}

export function getRawGuestItemsForMerge() {
  return loadRawGuestItems();
}
